import React from 'react';
import { useEstatePlanningWorkflowReviewQuery } from '../../api/useEstatePlanningWorkflowReviewQuery';
import { WorkflowWrapper } from '../../../Workflow/Workflow.styles';
import { Text } from '../../../CommonComponents/OPL/Text/Text';
import { Card } from '../../../CommonComponents/OPL/Card/Card';
import {
  List,
  ListDefinition,
  ListHeader,
  ListSubHeader,
  ListTerm,
} from './EstatePlanningWorkflowReview.styles';
import { formatCurrency, formatPercentage } from '../../../CommonUtils/formatNumber';
import { ErrorState } from '../../../CommonComponents/OPL/AsyncStates/ErrorState/ErrorState';
import { EstatePlanningWorkflowReviewLoading } from './EstatePlanningWorkflowReview.loading';
import { WorkflowComponentProps } from '../../../Workflow/Workflow.meta';
import { WorkflowFooter } from '../../../Workflow/components/WorkflowFooter/WorkflowFooter';
import { Lozenge } from '../../../CommonComponents/OPL/Lozenge/Lozenge';

interface Props {
  clientId: string;
  workflowProps: WorkflowComponentProps;
}

export const EstatePlanningWorkflowReview = ({ clientId, workflowProps }: Props) => {
  return (
    <>
      <WorkflowWrapper>
        <Text as="h1" fontSize="h5" fontWeight={600} m="0" mb="32">
          Please review your information
        </Text>

        <Content clientId={clientId} />
      </WorkflowWrapper>

      <WorkflowFooter
        isFirstStep={false}
        showNextButtonIcon={false}
        nextButtonText={
          <>
            <>Show My Estate Planning Flow Chart</>
            <Lozenge
              size="small"
              ml="4"
              css={`background: white; color ${({ theme }) =>
                theme.colors.primary['500']}; transform: scale(0.8)`}
            >
              BETA
            </Lozenge>
          </>
        }
        onNext={workflowProps.onNext}
        onPrevious={workflowProps.onPrevious}
      />
    </>
  );
};

interface ContentProps {
  clientId: string;
}

const Content = ({ clientId }: ContentProps) => {
  const { isLoading, isError, refetch, data } = useEstatePlanningWorkflowReviewQuery({ clientId });

  if (isLoading) {
    return <EstatePlanningWorkflowReviewLoading />;
  }

  if (isError) {
    return <ErrorState onAction={refetch} />;
  }

  if (!data) {
    throw new Error('No data in EstatePlanningWorkflowReview');
  }

  const {
    HouseholdInformation: {
      HasSpouse,
      PrincipalName,
      SpouseName,
      PrincipalAge,
      SpouseAge,
      HasChildren,
      Children,
      Trusts,
    },
    EstateTaxRate: {
      FilingState,
      StateTaxRate,
      StateTaxLowerLimitAmount,
      FederalTaxRate,
      FederalTaxLowerLimitAmount,

      SpouseFederalTaxLowerLimitAmount = null,
      SpouseFederalTaxRate,
      SpouseStateTaxRate,
      SpouseFilingState,
      SpouseStateTaxLowerLimitAmount,
    },
    DeductionAndGifts: {
      PrincipalAdministrationExpenseDeductionAmount,
      PrincipalLifetimeGiftingExemptionUsedAmount,
      SpouseAdministrationExpenseDeductionAmount,
      SpouseLifetimeGiftingExemptionUsedAmount,
    },
    Accounts,
    Assets,
    LifeInsurances,
  } = data;

  return (
    <>
      <Card>
        <ListHeader>Household Information</ListHeader>

        <List>
          <ListTerm>Partner</ListTerm>
          <ListDefinition>{HasSpouse ? 'Yes' : 'No'}</ListDefinition>
          {HasSpouse && (
            <>
              <ListTerm>Partner&apos;s Name</ListTerm>
              <ListDefinition>{SpouseName}</ListDefinition>
            </>
          )}
          <ListTerm>{`Age${HasSpouse ? 's' : ''}`}</ListTerm>
          <ListDefinition>
            {PrincipalAge}
            {HasSpouse ? `, ${SpouseAge}` : ''}
          </ListDefinition>

          <ListTerm>Children</ListTerm>
          <ListDefinition>{HasChildren ? 'Yes' : 'No'}</ListDefinition>

          {HasChildren && (
            <>
              <ListTerm>Children Name(s)</ListTerm>
              <ListDefinition>{Children}</ListDefinition>
            </>
          )}

          {Trusts.map(({ Name, Value }) => (
            <>
              <ListTerm>{Name}</ListTerm>
              <ListDefinition>{formatCurrency(Value, { mantissa: 2 })}</ListDefinition>
            </>
          ))}
        </List>

        <ListHeader>Estate Tax Rate</ListHeader>

        <List>
          <>
            <ListTerm>Filing State</ListTerm>
            <ListDefinition>
              {FilingState}
              {SpouseFilingState ? `, ${SpouseFilingState}` : null}
            </ListDefinition>
          </>

          {(StateTaxRate && StateTaxRate >= 0) ||
          (SpouseStateTaxRate && SpouseStateTaxRate >= 0) ? (
            <>
              <ListTerm>State Estate Tax Rate</ListTerm>
              <ListDefinition>
                {StateTaxRate && StateTaxRate >= 0
                  ? formatPercentage(StateTaxRate / 100, { mantissa: 2, optionalMantissa: true })
                  : SpouseStateTaxRate && SpouseStateTaxRate >= 0
                  ? 'N/A'
                  : ''}

                {SpouseStateTaxRate && SpouseStateTaxRate >= 0
                  ? `, ${formatPercentage(SpouseStateTaxRate / 100, {
                      mantissa: 2,
                      optionalMantissa: true,
                    })}`
                  : null}
              </ListDefinition>
            </>
          ) : null}

          {(StateTaxLowerLimitAmount && StateTaxLowerLimitAmount >= 0) ||
          (SpouseStateTaxLowerLimitAmount && SpouseStateTaxLowerLimitAmount >= 0) ? (
            <>
              <ListTerm>On Taxable Estates Over This Amount</ListTerm>
              <ListDefinition>
                {StateTaxLowerLimitAmount && StateTaxLowerLimitAmount >= 0
                  ? formatCurrency(StateTaxLowerLimitAmount, { decimalPlaces: 0 })
                  : SpouseStateTaxLowerLimitAmount && SpouseStateTaxLowerLimitAmount > 0
                  ? 'N/A'
                  : ''}

                {SpouseStateTaxLowerLimitAmount && SpouseStateTaxLowerLimitAmount >= 0
                  ? `, ${formatCurrency(SpouseStateTaxLowerLimitAmount, {
                      decimalPlaces: 0,
                    })}`
                  : null}
              </ListDefinition>
            </>
          ) : null}

          <ListTerm>Federal Estate Tax Rate</ListTerm>
          <ListDefinition>
            {formatPercentage(FederalTaxRate / 100, { mantissa: 2, optionalMantissa: true })}

            {!!SpouseFederalTaxRate && (
              <>
                {', '}
                {formatPercentage(SpouseFederalTaxRate / 100, {
                  mantissa: 2,
                  optionalMantissa: true,
                })}
              </>
            )}
          </ListDefinition>

          <ListTerm>On Taxable Estates Over This Amount</ListTerm>
          <ListDefinition>
            {formatCurrency(FederalTaxLowerLimitAmount, { decimalPlaces: 0 })}

            {SpouseFederalTaxLowerLimitAmount !== null && (
              <>
                {', '}
                {formatCurrency(SpouseFederalTaxLowerLimitAmount, {
                  decimalPlaces: 0,
                })}
              </>
            )}
          </ListDefinition>
        </List>

        <ListHeader>Deductions & Gifts</ListHeader>

        {HasSpouse && <ListSubHeader>{PrincipalName}</ListSubHeader>}

        <List>
          <ListTerm>Estate Administration Expense Deduction</ListTerm>
          <ListDefinition>
            {formatCurrency(PrincipalAdministrationExpenseDeductionAmount, { decimalPlaces: 0 })}
          </ListDefinition>

          <ListTerm>Lifetime Gifting Exemption Used</ListTerm>
          <ListDefinition>
            {formatCurrency(PrincipalLifetimeGiftingExemptionUsedAmount, { decimalPlaces: 0 })}
          </ListDefinition>
        </List>

        {HasSpouse && (
          <>
            <ListSubHeader>{SpouseName}</ListSubHeader>

            <List>
              {SpouseAdministrationExpenseDeductionAmount !== null && (
                <>
                  <ListTerm>Estate Administration Expense Deduction</ListTerm>
                  <ListDefinition>
                    {formatCurrency(SpouseAdministrationExpenseDeductionAmount, {
                      decimalPlaces: 0,
                    })}
                  </ListDefinition>
                </>
              )}

              {SpouseLifetimeGiftingExemptionUsedAmount !== null && (
                <>
                  <ListTerm>Lifetime Gifting Exemption Used</ListTerm>
                  <ListDefinition>
                    {formatCurrency(SpouseLifetimeGiftingExemptionUsedAmount, { decimalPlaces: 0 })}
                  </ListDefinition>
                </>
              )}
            </List>
          </>
        )}

        <ListHeader>Accounts</ListHeader>

        <List>
          <ListTerm>Accounts</ListTerm>
          <ListDefinition>{Accounts.TotalCount}</ListDefinition>

          <ListTerm>Liquid</ListTerm>
          <ListDefinition>{Accounts.LiquidCount}</ListDefinition>

          <ListTerm>Included in Gross Estate</ListTerm>
          <ListDefinition>{Accounts.IncludedInGrossEstateCount}</ListDefinition>

          <ListTerm>Total Amount</ListTerm>
          <ListDefinition>
            {formatCurrency(Accounts.TotalAmount, { decimalPlaces: 0 })}
          </ListDefinition>
        </List>

        <ListHeader>Assets</ListHeader>

        <List>
          <ListTerm>Assets</ListTerm>
          <ListDefinition>{Assets.TotalCount}</ListDefinition>

          <ListTerm>Illiquid</ListTerm>
          <ListDefinition>{Assets.IlliquidCount}</ListDefinition>

          <ListTerm>Included in Gross Estate</ListTerm>
          <ListDefinition>{Assets.IncludedInGrossEstateCount}</ListDefinition>

          <ListTerm>Total Amount</ListTerm>
          <ListDefinition>
            {formatCurrency(Assets.TotalAmount, { decimalPlaces: 0 })}
          </ListDefinition>
        </List>

        {LifeInsurances.length > 0 && (
          <>
            <ListHeader>Life Insurance</ListHeader>

            {LifeInsurances.map((insurance, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <React.Fragment key={index}>
                {LifeInsurances.length > 1 && <ListSubHeader>Policy {index + 1}</ListSubHeader>}

                <List>
                  <ListTerm>Owner</ListTerm>
                  <ListDefinition>{insurance.Owner}</ListDefinition>

                  <ListTerm>Insured</ListTerm>
                  <ListDefinition>{insurance.Insured}</ListDefinition>

                  <ListTerm>Cash Value</ListTerm>
                  <ListDefinition>
                    {formatCurrency(insurance.CashValue, { decimalPlaces: 0 })}
                  </ListDefinition>

                  <ListTerm>Death Benefit</ListTerm>
                  <ListDefinition>
                    {formatCurrency(insurance.DeathBenefit, { decimalPlaces: 0 })}
                  </ListDefinition>

                  <ListTerm>Included in Gross Estate</ListTerm>
                  <ListDefinition>{insurance.IncludeInGrossEstate ? 'Yes' : 'No'}</ListDefinition>
                </List>
              </React.Fragment>
            ))}
          </>
        )}
      </Card>
    </>
  );
};
